/* istanbul ignore file */
import { useMemo } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useIdentity } from '@zenbusiness/application-commons-identity';

import {
  ENABLE_MEETING_MINUTES_JOURNEY,
  ENABLE_ORDER_HISTORY_V2,
  ENABLE_ZERO_STAGE_TAX_SERVICES_PAGE,
  RELEASE_BENEFICIAL_OWNERSHIP_PAGE,
  RELEASE_STANDALONE_INVOICING,
  RELEASE_WEB_TOOLS_SINGLE_LEFT_NAV_MENU_ITEM,
  SIMPLIFY_ONBOARDING_DOMAIN_DASHBOARD_SWITCH
} from '#client/constants/experimentsFlags';

import { getMenuConfig } from '../config';
import { useGoogleBusinessConfig } from './useGoogleBusinessConfig';

/**
 * Return the menu config for the current business entity.
 *
 * @param {boolean} isMobile
 * @returns {{
 *  loading: boolean
 *  menu: Array
 * }}
 */
export function useBusinessEntityNav({ isMobile }) {
  const { accountEmail: email } = useIdentity();
  const { businessEntityUuid, entitlements, loading } = useBusinessEntity();

  const { isGoogleBusinessProfilePhase2Released } = useGoogleBusinessConfig();

  const isSimplifiedOnboardingReleased = useExperimentFlag(
    SIMPLIFY_ONBOARDING_DOMAIN_DASHBOARD_SWITCH,
    false
  );
  const enableZeroStageTaxServicesPage = useExperimentFlag(
    ENABLE_ZERO_STAGE_TAX_SERVICES_PAGE,
    false
  );
  const enableOrderHistoryV2 = useExperimentFlag(ENABLE_ORDER_HISTORY_V2, false);
  const releaseBeneficialOwnershipPage = useExperimentFlag(
    RELEASE_BENEFICIAL_OWNERSHIP_PAGE,
    false
  );
  const releaseMeetingMinutesPage = useExperimentFlag(ENABLE_MEETING_MINUTES_JOURNEY, false);
  const releaseStandaloneInvoicing = useExperimentFlag(RELEASE_STANDALONE_INVOICING, false);
  const releaseWebToolsSingleLeftNavMenuItem = useExperimentFlag(
    RELEASE_WEB_TOOLS_SINGLE_LEFT_NAV_MENU_ITEM,
    false
  );

  // Need to memoize the ldFlags if we want to memoize the menu
  const ldFlags = useMemo(
    () => ({
      enableOrderHistoryV2,
      enableZeroStageTaxServicesPage,
      isGoogleBusinessProfilePhase2Released,
      isSimplifiedOnboardingReleased,
      releaseBeneficialOwnershipPage,
      releaseMeetingMinutesPage,
      releaseWebToolsSingleLeftNavMenuItem,
      releaseStandaloneInvoicing
    }),
    [
      enableOrderHistoryV2,
      enableZeroStageTaxServicesPage,
      isGoogleBusinessProfilePhase2Released,
      isSimplifiedOnboardingReleased,
      releaseBeneficialOwnershipPage,
      releaseMeetingMinutesPage,
      releaseWebToolsSingleLeftNavMenuItem,
      releaseStandaloneInvoicing
    ]
  );

  const menu = useMemo(
    () => getMenuConfig({
      businessEntityUuid,
      email,
      entitlements,
      isMobile,
      ldFlags
    }),
    [businessEntityUuid, email, entitlements, isMobile, ldFlags]
  );

  return { loading, menu };
}
